import React, { useState } from 'react'
import { FaMinus, FaPlus } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { addItem, removeItem } from '../../../store/slices/cartSlice'
import { WR } from '../../../components/ui/tokens'
import { resolveIconUrl } from '../../../utils/iconUrl'
import useLongPress from '../productLayout/useLongPress'

// VISUAL RELOOKING ONLY — same data (backend icon/name/price) and the same
// addItem/removeItem dispatches + quantity/disabled logic as before.
// `editing`  : layout edit mode — the −/+ steppers are disabled and the tile
//              is only a drag handle (SortableProductGrid wraps it).
// `onOpenMenu({ x, y })` : opens the layout context menu on right-click or
//              long-press (touch). Absent = no menu on this tile.
const Choose = (props) => {
  const lang = localStorage.getItem("lang");
  const { editing = false, onOpenMenu } = props
  const { id, name, name_fr, name_nl, price, icon, dark_icon, photo } = props.data
  const dispatch = useDispatch()
  const [iconIdx, setIconIdx] = useState(0)

  const cartSelectedItem = useSelector(state => state.cart.selectedItems)

  const filterId = cartSelectedItem?.filter(el => el.id === id)
  const qty = filterId?.[0]?.quantity ?? 0
  const displayName = lang === `french` ? name_fr : lang === `dutch` ? name_nl : name
  // Prefer the dark icon, then the standard icon, then the photo — whichever
  // the backend actually serves and loads. Monogram only if all fail.
  const iconCandidates = [dark_icon, icon, photo].filter(Boolean)
  const iconUrl = resolveIconUrl(iconCandidates[iconIdx])

  const handleAddItem = () => {
    dispatch(addItem(id));
  };

  const handleRemoveItem = () => {
    dispatch(removeItem(id));
  };

  const canOpenMenu = typeof onOpenMenu === 'function' && !editing
  const longPress = useLongPress(
    (point) => onOpenMenu(point),
    { enabled: canOpenMenu }
  )
  const handleContextMenu = (event) => {
    if (!canOpenMenu) return
    event.preventDefault()
    onOpenMenu({ x: event.clientX, y: event.clientY })
  }

  const stepBtn = (variant) => ({
    width: 30,
    height: 30,
    borderRadius: WR.radius.pill,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 0,
    flexShrink: 0,
    background: variant === 'inc' ? WR.color.brand : WR.color.lavSoft,
    color: variant === 'inc' ? '#fff' : WR.color.brand,
    boxShadow: variant === 'inc' ? `0 3px 8px -2px ${WR.color.brand}` : 'none',
  });

  return (
    <div
      data-testid={`product-tile-${id}`}
      onContextMenu={handleContextMenu}
      {...(canOpenMenu ? longPress : {})}
      style={{
        background: WR.color.surface,
        border: `1px solid ${WR.color.border}`,
        borderRadius: WR.radius.card,
        padding: 13,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
        fontFamily: WR.font.sans,
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '1.5',
          borderRadius: WR.radius.md,
          background: WR.color.lavSel,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {iconUrl ? (
          <img
            alt=''
            src={iconUrl}
            onError={() => setIconIdx((i) => i + 1)}
            style={{ maxWidth: '72%', maxHeight: '72%', objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: 28, fontWeight: 800, color: WR.color.brand }}>
            {(displayName || '?').trim().charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: WR.text.body,
          fontWeight: WR.weight.bold,
          letterSpacing: -0.1,
          color: WR.color.encre,
        }}
      >
        {displayName}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginTop: 'auto',
        }}
      >
        <span
          style={{
            fontSize: WR.text.slot,
            fontWeight: WR.weight.extra,
            color: WR.color.encre,
            whiteSpace: 'nowrap',
          }}
        >
          {'€'}{price}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <button
            aria-label="decrement"
            style={{
              ...stepBtn('dec'),
              cursor: !editing && qty > 0 ? 'pointer' : 'not-allowed',
              opacity: !editing && qty > 0 ? 1 : 0.45,
            }}
            disabled={editing || qty === 0}
            onClick={() => handleRemoveItem()}
          >
            <FaMinus size={13} />
          </button>
          <span
            style={{
              minWidth: 14,
              textAlign: 'center',
              fontSize: WR.text.slot,
              fontWeight: WR.weight.extra,
              color: WR.color.encre,
            }}
          >
            {qty}
          </span>
          <button
            aria-label="increment"
            style={{ ...stepBtn('inc'), cursor: editing ? 'not-allowed' : 'pointer', opacity: editing ? 0.45 : 1 }}
            disabled={editing}
            onClick={() => handleAddItem()}
          >
            <FaPlus size={13} />
          </button>
        </span>
      </div>
    </div>
  )
}

export default Choose
